import * as React from 'react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

/**
 * A small info affordance that reveals its content on **click/tap** rather than
 * hover — so it's reachable on touch, where hover (including Radix tooltips) is
 * dead. Matches the site's "help = click" convention (the draft-award trophy and
 * roster salary breakdown use the same pattern). Use this for any hover-tooltip
 * whose content a touch user genuinely needs.
 *
 * The trigger is a real `<button>` (keyboard + tap accessible) and stops click
 * propagation so tapping it inside a clickable row/cell doesn't also trigger the
 * row. Pass the visible cue (an abbreviation, icon, badge, value…) as children
 * and the revealed detail as `content`.
 */
export function InfoTip({
  content,
  children,
  className,
  contentClassName,
  align = 'start',
  ariaLabel,
}: {
  content: React.ReactNode
  children: React.ReactNode
  /** Classes for the trigger button (carry the existing cue styling here). */
  className?: string
  /** Classes for the popover panel. */
  contentClassName?: string
  align?: 'start' | 'center' | 'end'
  ariaLabel?: string
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          onClick={(e) => e.stopPropagation()}
          className={cn('cursor-pointer text-left', className)}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className={cn('w-auto max-w-[16rem] px-3 py-1.5 text-xs leading-snug', contentClassName)}
      >
        {content}
      </PopoverContent>
    </Popover>
  )
}
